import request from '@flowio/request';
import session from '@flowio/session';
import { stringify } from '@flowio/web-sdk';
import get from 'lodash/get';
import assign from 'lodash/assign';
import find from 'lodash/find';
import map from 'lodash/map';
import isNil from 'lodash/isNil';
import { getFeatures, isFeatureInResultEnabled } from './features';
import { initializeCheckoutWithOrderForm, initializeCheckoutWithOrderNumber, initializeCheckoutWithShopifyCart } from './checkout';
import CartElements from './cart-elements';
import { init as cartInit } from './cart_unified/cart-unified';
import config from './config';
import events from './events';
import optionsError from './utilities/options-error';
import optionsSuccess from './utilities/options-success';
import { getCheckoutByOrderNumber, getCheckoutByOrderForm, getOrderUrl } from './orders';
import globalFeatures from './constants/features';
import { locationRedirect } from './utilities/location-redirect';
import showErrorModal from './utilities/error-modal';
import { openCheckout, setCheckoutItems, isEnabled as isBlazeEnabled } from './blaze';

const EventConstants = events.constants;
// Stores a reference to the cart for performance reasons
let cartReference;
let cartElementsInstance;
let cartUnifiedInstance;


/** @description method set contents of Blaze line items. */
function setBlazeLineItems(/** @type {import('@flowio/types/dist/api').ShopifyCart} */shopifyCart) {
  isBlazeEnabled().then((enabled) => {
    if (enabled) {
      setCheckoutItems(shopifyCart.items.map(item => ({
        number: item.variant_id,
        quantity: item.quantity,
        attributes: item.properties,
      })));
    }
  });
}

function getCartId() {
  const sess = session.getSession();

  if (sess && sess.attributes && sess.attributes.cart_id) {
    return sess.attributes.cart_id;
  }

  // TODO: Deprecated -- remove when sess service deployed
  if (sess && sess.cart && sess.cart.id) {
    return sess.cart.id;
  }

  return undefined;
}

/**
 * Store the most recent state of the cart
 * @param {Object} cart - The response from the cart API, a `shopify_cart` model
 */
function setCartReference(cart = {}) {
  if (cart.id && cart.items) {
    cartReference = cart;
    setBlazeLineItems(cart);
  }
}

/**
 * Determines if an item exists currently in the cart.
 *
 * @param {Object} cart - The response from the cart API, a `shopify_cart` model
 * @param {Number|String} id - flow cart item number or shopify variant id
 */
function cartHasItem(cart, id) {
  if (!cart) {
    return false;
  }

  try {
    if (id <= cart.items.length) {
      return true;
    }
  } catch (error) {
    return false;
  }

  return !!cart.items.find(item => String(item.variant_id) === String(id));
}

function handleResponse(status, response, options = {}) {
  const isResponseError = !!(response.error && options.error);
  const isErrorInstance = !!(response instanceof Error && options.error);

  if (isResponseError) {
    options.error(status, response.error);
  } else if (isErrorInstance) {
    options.error(status, response);
  } else if (options.success) {
    options.success(status, response);
  }
}

/**
 * Wrapper around request call to dispatch dom events
 */
function makeRequest(options = {}, callback) {
  const requestOptions = {
    ...options,
    failOnMissingCredentials: true,
  };

  request.authenticated(requestOptions, (status, response) => {
    callback(status, response);
  });
}

/**
 * Retrieve cart from API
 *
 * @param {Object} options              Optional. Default: {}.
 * @param {function} options.success    Optional. success(status, cart)
 * @param {function} options.error      Optional. error(status, error)
 */
function getCart(options = {}) {
  const cartId = getCartId();

  makeRequest({
    url: `${config.API_BASE_URI}/shopify/carts/${cartId}`,
  }, (status, response) => {
    setCartReference(response);
    handleResponse(status, response, options);
  });
}

/**
 * Get the current cart reference. If it isn't set yet, fetch the cart first.
 * @return {Promise<import('@flowio/types/dist/api').ShopifyCart>}
 */
function getCartReference() {
  if (!cartReference) {
    return new Promise((resolve, reject) => {
      getCart({
        success: (status, cart) => {
          setCartReference(cart);
          resolve(cartReference);
        },
        error: error => reject(error),
      });
    });
  }

  return Promise.resolve(cartReference);
}

/**
 * Add item to cart
 * @param {number} id                   The variant id of the item to add to to cart
 * @param {number} quantity             Optional. Default: 1.
 * @param {Object} properties           Not Implemented
 * @param {Object} options              Optional. Default: {}.
 * @param {function} options.success    Optional. success(status, cart)
 * @param {function} options.error      Optional. error(status, error)
 */
function addItem(id, quantity, properties = {}, options = {}) {
  const cartId = getCartId();
  const opts = assign({ eventsEnabled: true }, options);
  const qty = quantity || 1;

  if (quantity === 0) {
    getCartReference().then(cart => handleResponse(200, cart, opts));
    return;
  }

  makeRequest({
    url: `${config.API_BASE_URI}/shopify/carts/${cartId}/add`,
    method: 'POST',
    data: {
      discriminator: 'shopify_cart_add_single_form',
      id,
      quantity: qty,
      properties,
    },
  },
  /** @type {(status: number, response: import('@flowio/types/dist/api').ShopifyCart)} */
  (status, response) => {
    setCartReference(response);
    handleResponse(status, response, opts);

    if (opts.eventsEnabled) {
      events.trigger(EventConstants.CART_ADD_ITEM, {
        id,
        quantity: qty,
        cart: response,
      });
      events.trigger(EventConstants.CART_UPDATED, { cart: response });
    }
  });
}

/**
 * Adds multiple items to the cart at once.
 *
 * @param {[Object]} items   Array of { id: Number, quantity: Number, properties: Object }
 * @param {Object} options              Optional. Default: {}.
 * @param {function} options.success    Optional. success(status, cart)
 * @param {function} options.error      Optional. error(status, error)
 */
function addItems(items, options = {}) {
  const cartId = getCartId();
  const opts = assign({ eventsEnabled: true }, options);

  makeRequest({
    url: `${config.API_BASE_URI}/shopify/carts/${cartId}/add`,
    method: 'POST',
    data: {
      discriminator: 'shopify_cart_add_multiple_form',
      items,
    },
  },
  /** @type {(status: number, response: import('@flowio/types/dist/api').ShopifyCart)} */
  (status, response) => {
    setCartReference(response);
    handleResponse(status, response, opts);

    if (opts.eventsEnabled) {
      events.trigger(EventConstants.CART_ADD_ITEMS, {
        items,
        cart: response,
      });
      events.trigger(EventConstants.CART_UPDATED, { cart: response });
    }
  });
}

/**
 * Update cart item by variant id.
 *
 * @param {number|string} id            The variant id of the item
 * @param {number} quantity             Updated quantity for item
 * @param {Object} properties           Not Implemented.
 * @param {Object} options              Optional. Default: {}.
 * @param {function} options.success    Optional. success(status, cart)
 * @param {function} options.error      Optional. error(status, error)
 */
function updateItem(id, quantity, properties, options = {}) {
  const cartId = getCartId();

  const callAPI = () => {
    makeRequest({
      url: `${config.API_BASE_URI}/shopify/carts/${cartId}/change`,
      method: 'POST',
      data: {
        id,
        quantity,
        properties,
      },
    }, (status, response) => {
      setCartReference(response);
      handleResponse(status, response, options);
      if (quantity === 0) {
        events.trigger(EventConstants.CART_REMOVE_ITEM, {
          id,
          cart: response,
        });
      } else {
        events.trigger(EventConstants.CART_UPDATE_ITEM, {
          id,
          quantity,
          cart: response,
        });
      }
      events.trigger(EventConstants.CART_UPDATED, { cart: response });
    });
  };

  getCartReference().then((cart) => {
    if (quantity === 0 && !cartHasItem(cart, id)) {
      handleResponse(200, cart, options);
      return;
    }
    callAPI();
  }).catch(() => {
    callAPI();
  });
}

/**
 * Remove an item from the cart by variant id.
 *
 * @param {number|string} id         The variant id of the item
 * @param {Object} options              Optional. Default: {}.
 * @param {function} options.success    Optional. success(status, cart)
 * @param {function} options.error      Optional. error(status, error)
 */
function removeItem(id, options = {}) {
  updateItem(id, 0, {}, options);
}

/**
 * Remove all items from the cart.
 *
 * @param {Object} options              Optional. Default: {}.
 * @param {function} options.success    Optional. success(status, cart)
 * @param {function} options.error      Optional. error(status, error)
 */
function clear(options = {}) {
  const cartId = getCartId();

  makeRequest({
    url: `${config.API_BASE_URI}/shopify/carts/${cartId}/clear`,
    method: 'POST',
  }, (status, response) => {
    setCartReference(response);
    handleResponse(status, response, options);
    events.trigger(EventConstants.CART_CLEAR);
    events.trigger(EventConstants.CART_UPDATED, { cart: response });
  });
}

function localize(options = {}) {
  if (isNil(cartElementsInstance)) {
    cartElementsInstance = new CartElements();
  }

  return new Promise((resolve) => {
    cartElementsInstance.localize(options).then(resolve, resolve);
  });
}

/**
 * Get HTML representation of cart.
 *
 * @param {Object} options              Optional. Default: {}.
 * @param {function} options.success    Optional. success(status, cart)
 * @param {function} options.error      Optional. error(status, error)
 */


function getCartHtml(options = {}) {
  const cartId = getCartId();

  makeRequest({
    url: `${config.API_BASE_URI}/shopify/carts/${cartId}.html`,
  }, (status, response) => {
    handleResponse(status, response, options);
    events.trigger(EventConstants.CART_HTML, { html: response });
  });
}

function setGeo(geo, options = {}) {
  const cartId = getCartId();

  makeRequest({
    url: `${config.API_BASE_URI}/shopify/carts/${cartId}/geo`,
    method: 'PUT',
    data: geo,
  }, (status, response) => {
    setCartReference(response);
    handleResponse(status, response, options);
  });
}

function applyPromotion(code, options = {}) {
  const cartId = getCartId();

  makeRequest({
    url: `${config.API_BASE_URI}/shopify/carts/${cartId}/promotion`,
    method: 'PUT',
    data: {
      code,
    },
  }, (status, response) => {
    handleResponse(status, response, options);
  });
}

function removePromotion(code, options = {}) {
  const cartId = getCartId();

  makeRequest({
    url: `${config.API_BASE_URI}/shopify/carts/${cartId}/promotion`,
    method: 'DELETE',
  }, (status, response) => {
    handleResponse(status, response, options);
  });
}

/**
 * Get path to the endpoint that will convert a cart to an order for checkout
 */
function getCheckoutPath() {
  const prefix = config.SHOPIFY_CHECKOUT_PATH_PREFIX;
  const cartId = getCartId();
  const organizationId = config.FLOW_ORGANIZATION;
  return `${prefix}/${organizationId}/cart/${cartId}/order`;
}

function getOrderFormCheckoutPath(form = {}) {
  const prefix = config.SHOPIFY_CHECKOUT_PATH_PREFIX;
  const organizationId = config.FLOW_ORGANIZATION;
  const query = {
    ...form,
    country: session.getCountry(),
    flow_session_id: session.getSessionId(),
  };

  return `${prefix}/${organizationId}/shopify/direct?${stringify(query)}`;
}

/**
 * Redirect user to the cart page.
 */
function redirectToCart() {
  const prefix = config.SHOPIFY_CHECKOUT_PATH_PREFIX;
  const cartId = getCartId();
  const organizationId = config.FLOW_ORGANIZATION;

  getFeatures().then((featureValuesResult) => {
    const isReturningCustomersEnabled = isFeatureInResultEnabled(
      globalFeatures.RETURNING_CUSTOMERS,
      featureValuesResult,
    );

    const isCheckoutInitializeEnabled = isFeatureInResultEnabled(
      globalFeatures.CHECKOUT_INITIALIZE_ENDPOINT,
      featureValuesResult,
    );

    if (isReturningCustomersEnabled || isCheckoutInitializeEnabled) {
      initializeCheckoutWithShopifyCart(cartId);
    } else {
      window.location.href = `${prefix}/${organizationId}/cart/${cartId}`;
    }
  });
}

/**
 * Redirect user to convert their cart to a Flow order and then take them to the checkout page
 */
function redirectCartToCheckout(options = {}) {
  const checkoutPath = getCheckoutPath();

  const {
    forceLogin,
    processRedirect = true,
  } = options;

  if (processRedirect) {
    if (forceLogin) {
      window.location.href = `/account/login?checkout_url=${encodeURIComponent(checkoutPath)}`;
    } else {
      window.location.href = checkoutPath;
    }
  }
}

function redirectOrderFormToCheckout(options = {}) {
  const { orderForm, processRedirect = true } = options;
  const checkoutUrl = getOrderFormCheckoutPath(orderForm);
  const itemsForForm = map(orderForm.items, item => ({ id: item.number, quantity: item.quantity }));

  addItems(itemsForForm, {
    success: () => {
      optionsSuccess(options);
      if (process.env.NODE_ENV === 'test') {
        return;
      }

      // Flag to allow for disabling redirect in headless chrome
      if (processRedirect) {
        window.location.href = checkoutUrl;
      }
    },
    error: (status, error) => {
      optionsError(options, error);
    },
  });
}

function redirectCheckout(checkout) {
  const checkoutUrl = getOrderUrl({ checkoutId: checkout.id });
  locationRedirect(checkoutUrl);
}

/**
 * Redirect user to checkout page.
 */
function redirectCheckoutsOrderNumber(orderNumber) {
  getCheckoutByOrderNumber(orderNumber)
    .then(redirectCheckout)
    .catch((error) => {
      showErrorModal(error);
    });
}

function redirectCheckoutsOrderForm(orderForm) {
  getCheckoutByOrderForm(orderForm)
    .then(redirectCheckout)
    .catch((error) => {
      showErrorModal(error);
    });
}

/**
 * Redirect user to checkout page.
 *
 * This is for Cart V1. See `checkout.js` for CartV2 checkout redirect
 */
function redirectToCheckout(options = {}) {
  const { orderForm } = options;
  Promise.all([
    getFeatures(),
    isBlazeEnabled(),
  ])
  .then(([featureValuesResult, blazeEnabled]) => {
    const isCheckoutResourceEnabled = isFeatureInResultEnabled(
      globalFeatures.CHECKOUT_RESOURCE,
      featureValuesResult,
    );

    const isReturningCustomersEnabled = isFeatureInResultEnabled(
      globalFeatures.RETURNING_CUSTOMERS,
      featureValuesResult,
    );

    const isCheckoutInitializeEnabled = isFeatureInResultEnabled(
      globalFeatures.CHECKOUT_INITIALIZE_ENDPOINT,
      featureValuesResult,
    );

    if (blazeEnabled) {
      getCartReference().then((/** @type {import('@flowio/types/dist/api').ShopifyCart} */cart) => {
        openCheckout(cart.items.map(item => ({
          number: item.variant_id,
          quantity: item.quantity,
          attributes: item.properties,
        })));
      });
    } else if (isReturningCustomersEnabled || isCheckoutInitializeEnabled) {
      if (orderForm) {
        initializeCheckoutWithOrderForm(orderForm);
      } else {
        initializeCheckoutWithOrderNumber(getCartId());
      }
    } else if (isCheckoutResourceEnabled) {
      if (orderForm) {
        redirectCheckoutsOrderForm(orderForm);
      } else {
        redirectCheckoutsOrderNumber(getCartId());
      }
    } else if (orderForm) {
      redirectOrderFormToCheckout(options);
    } else {
      redirectCartToCheckout(options);
    }
  });
}

function getAttributes(options = {}) {
  getCart({
    success: (status, response) => {
      if (options.success) {
        options.success(status, get(response, 'attributes', {}));
      }
    },
    error: (status, response) => {
      if (options.error) {
        options.error(status, response.error);
      }
    },
  });
}

function getAttribute(attributeKey, opts = {}) {
  getAttributes({
    success: (status, attributes) => {
      if (opts.success) {
        opts.success(status, get(attributes, attributeKey));
      }
    },
    error: (status, response) => {
      if (opts.error) {
        opts.error(status, response.error);
      }
    },
  });
}

function setAttribute(attributeKey, attributeValue, options = {}) {
  const cartId = getCartId();
  const url = `${config.API_BASE_URI}/shopify/carts/${cartId}/attributes`;

  makeRequest({
    url,
    method: 'PUT',
    data: { attributes: { [attributeKey]: attributeValue } },
  }, (status, response) => {
    setCartReference(response);
    handleResponse(status, response, options);
  });
}

function setAttributes(attributes, options = {}) {
  const cartId = getCartId();

  makeRequest({
    url: `${config.API_BASE_URI}/shopify/carts/${cartId}/attributes`,
    method: 'PUT',
    data: { attributes },
  }, (status, response) => {
    setCartReference(response);
    handleResponse(status, response, options);
  });
}

function getThemeMoneyFormat() {
  const themeMoneyFormatPaths = [
    'Shopify.money_format', // Shopify API JS
    'theme.strings.moneyFormat', // Theme: Brooklyn
  ];
  const nativeMoneyFormatPath = find(themeMoneyFormatPaths, path => get(window, path));
  return get(window, nativeMoneyFormatPath);
}

function createMoneyFormatFromItem(item) {
  if (item) {
    const priceLabel = get(item, 'price.label');
    return priceLabel.replace(/[0-9\.,]+/, '{{amount}}');
  }
  return getThemeMoneyFormat();
}

/**
 * @description Get a Flow localized (flowcalized?) representation of the Shopify Cart
 * @param {object} [options] Options object
 * @param {function} [options.success] Success callback
 * @param {object} [options.error] error callback
 * @param {object} [options.cart] Shopify cart object to localize ...
 * (will be requested from /cart.js if not present)
 * @returns {Promise}
 */
function getShopifyLocalizedCart(options = {}) {
  if (isNil(cartUnifiedInstance)) {
    cartUnifiedInstance = cartInit({
      flowApiBaseURI: 'https://api.flow.io',
    });
  }

  if (!session.getExperience() && !isNil(options.cart)) {
    const result = { cart: options.cart, moneyFormat: getThemeMoneyFormat() };
    optionsSuccess(options, result);
    return Promise.resolve(result);
  }

  const cartPromise = isNil(options.cart)
    ? new Promise((resolve, reject) => {
      cartUnifiedInstance.shopifyCart.getCart({
        onSuccess: cartData => resolve(cartData),
        onError: e => reject(e),
      });
    })
    : Promise.resolve(options.cart);


  const onError = reject => (err) => {
    reject(err);
    optionsError(options);
  };

  return new Promise((resolve, reject) => {
    cartPromise.then((cartData) => {
      cartUnifiedInstance.shopifyCart.getFlowOrderFromCart(cartData, {
        onSuccess: (response) => {
          const cart = get(response, 'shopify.cart');
          if (isNil(cart)) {
            reject(new Error('Error retrieving cart.'));
            return;
          }
          const result = { cart: response.shopify.cart, moneyFormat: createMoneyFormatFromItem(get(response, 'flow.order.lines[0]')) };
          resolve(result);
          optionsSuccess(options, result);
        },
        onError: onError(reject),
      });
    }).catch(onError(reject));
  });
}

module.exports = {
  getCheckoutPath,
  getOrderFormCheckoutPath,
  redirectToCart,
  redirectToCheckout,
  getCart,
  getCartReference,
  getCartHtml,
  getShopifyLocalizedCart,
  setGeo,
  applyPromotion,
  removePromotion,

  // CartJS API
  addItem,
  addItems,
  updateItem,
  removeItem,
  clear,
  localize,
  getAttribute,
  getAttributes,
  setAttribute,
  setAttributes,
};



// WEBPACK FOOTER //
// ./static/js/v0/cart.js